import Vue from 'vue';
import VueSimpleSpinner from 'vue-simple-spinner';
import {HTTP} from '@/utils/httpCommonUtil';
import format from 'date-fns/format';
import {UserRoleEnum} from '@/enums/userRolesEnum';
import InvoiceEventHub from '@/components/invoice/InvoiceEventHub';
import HttpMixin from '@/mixins/HttpMixin';
import WarningModal from '@/componentsReusable/WarningModal.vue';
import GenericModal from "@/componentsReusable/genericModal.vue";

export default Vue.component('invoice-info-card', {
  name: 'InvoiceInfoCard',
  props: {
    'facilityId': {type: String},
    'specialtyId': {type: String},
    'invoiceDate': {type: String},
    'invoiceId': {type: [String, Number]},
    'mergedInvoiceId': {type: [String, Number]}
  },
  components: {
    VueSimpleSpinner,
    WarningModal,
    GenericModal
  },
  mixins: [HttpMixin],
  data() {
    return {
      cardExpanded: false,
      showConfirmationModal: false,
      showIgnoreQbSyncConfirmationModal: false,
      loadingInitialData: false,
      invoiceInfo: null as any,
      pdfInfo: null,
      showSubmissionEmailsSentModal: false,
      submissionEmailSentMessage: ''
    };
  },
  computed: {
    canCreate(): boolean {
      return Boolean(this.invoiceInfo && !this.invoiceInfo.invoiceId && this.invoiceInfo.invoiceEndDate && new Date(this.invoiceInfo.invoiceEndDate).getTime() < new Date().getTime());
    },
    invoiceExists(): boolean {
      return Boolean(this.invoiceInfo && this.invoiceInfo.invoiceId);
    },
    isLocked(): boolean {
      return Boolean(this.invoiceInfo && this.invoiceInfo.isLocked);
    },
    isAllowUnlockInvoice(): boolean {
      return Boolean(this.invoiceInfo && this.invoiceInfo.allowUnlockInvoice);
    },
    isFacilityUser(): boolean {
      return this.$store.getters['user/hasOnlyRole'](UserRoleEnum.FACILITY_CONTACT);
    }
  },
  async created() {
    await this.expandCard();
    InvoiceEventHub.$on('INVOICE_NOTE_CHANGE', this.dataFetchWrapper);
  },
  beforeDestroy: function () {
    InvoiceEventHub.$off('INVOICE_NOTE_CHANGE', this.dataFetchWrapper);
  },
  filters: {
    dateSubmitted(dateISO: string) {
      if (!dateISO) return '';
      // const date = convertDateStringToUTCDate(dateISO);
      return format(dateISO, 'MM.DD.YYYY, h:mm a');
    },
  },
  methods: {
    // this comment copypasta is to denote that this function is to work around a leak caused by using anonymous functions in the event callback.
    // per this issue comment https://github.com/vuejs/vue-router/issues/1181#issuecomment-281084486
    // testing bears this out to be true and was causing unnecessary data fetches as well as bugs with incorrect route redirects
    dataFetchWrapper() {
      this.getInvoiceInfo().then(() => {}).catch(() => '');
    },
    async expandCard(): Promise<void> {
      this.cardExpanded = !this.cardExpanded;
      if (this.cardExpanded) {
        await this.getInvoiceInfo();
      }
    },
    async getInvoiceInfo() {
      let showLoadingSpinnerTimeout = setTimeout(() => {
        this.loadingInitialData = true;
      }, 0);
      try {
        let parameters;
        if (this.invoiceId) {
          parameters = `invoiceId=${this.invoiceId}`;
        } else if (this.mergedInvoiceId) {
          parameters = `mergedInvoiceId=${this.mergedInvoiceId}`;
        } else if (this.facilityId && this.specialtyId && this.invoiceDate) {
          parameters = `facilityId=${this.facilityId}&specialtyId=${this.specialtyId}&invoiceDate=${this.invoiceDate}`;
        } else {
          throw new Error("Error retrieving Invoice Information");
        }
        const response = await HTTP.get('/api/invoices/info?' + parameters);
        clearTimeout(showLoadingSpinnerTimeout);
        this.loadingInitialData = false;
        if (!response.data || !response.data.data) {
          throw new Error("Error retrieving Invoice Information.");
        }
        this.invoiceInfo = response.data.data[0];
        if (this.invoiceInfo) {
          if (this.invoiceInfo.invoiceId) {
            if (!this.invoiceId) {
              this.$router.replace({name: 'Invoice', params: {invoiceId: this.invoiceInfo.invoiceId}});
              return;
            }
            await this.loadPdf();
          }
          this.invoiceInfo.isPastDue = (new Date(this.invoiceInfo.invoiceDueDate)).getTime() < (new Date()).getTime();
        }
        this.$emit('invoice-info-loaded', this.invoiceInfo);
      } catch (err) {
        clearTimeout(showLoadingSpinnerTimeout);
        this.loadingInitialData = false;
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred retrieving Invoice Information.'
        });
      }
    },
    async loadPdf() {
      const response = await HTTP.get('/api/invoices/' + this.invoiceId + '/pdf');
      if (!response.data || !response.data.data) {
        console.error('No PDF found');
      }
      this.pdfInfo = response.data.data;
    },
    browseConfirmed() {
      this.browseToFacility();
    },
    browseToFacility() {
      let url = "/facilities/" + (this.invoiceInfo as any).facilityId;
      const specialties = new Set<string>(this.invoiceInfo.invoiceWeeks.map((invoiceWeek:any) => invoiceWeek.specialtyId));
      if (specialties.size === 1) {
        url += "/specialties/" + (this.invoiceInfo.invoiceWeeks[0] as any).specialtyId
      }
      window.open(url, '_blank');
    },
    async generateInvoice(overrideValidation: boolean) {
      let showLoadingSpinnerTimeout = setTimeout(() => {
        this.loadingInitialData = true;
      }, 0);
      try {
        const isValid = await this.isValid();
        if (!overrideValidation && !isValid) {
          this.loadingInitialData = false;
          return;
        }
        this.showConfirmationModal = false;
        const toInsert = this.mergedInvoiceId
          ? {mergedInvoiceId: Number(this.mergedInvoiceId)}
          : {
            facilityId: (this.invoiceInfo as any).facilityId,
            specialtyId: (this.invoiceInfo.invoiceWeeks[0] as any).specialtyId,
            invoiceStartDate: (this.invoiceInfo.invoiceWeeks[0] as any).invoiceStartDate
          };
        const response = await HTTP.post('/api/invoices', toInsert);
        clearTimeout(showLoadingSpinnerTimeout);
        this.loadingInitialData = false;
        if (response.data && !response.data.success && response.data.message) {
          this.$notify({
            group: 'main',
            type: 'error',
            title: 'Error',
            text: response.data.message
          });
          return;
        } else if (!response.data || !response.data.data) {
          throw new Error("Error generating Invoice.");
        }
        this.$router.push({name: 'Invoice', params: {invoiceId: response.data.data.invoiceId}});
      } catch (err) {
        console.error(err);
        clearTimeout(showLoadingSpinnerTimeout);
        this.loadingInitialData = false;
        let errorMessage = 'An error occurred generating the Invoice.';
        if (err.response.data && err.response.data.message && err.response.data.message.length) {
          errorMessage = err.response.data.message;
        }
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: errorMessage
        });
      }
    },
    async isValid(): Promise<boolean> {
      const params = this.mergedInvoiceId
          ? `mergedInvoiceId=${this.mergedInvoiceId}`
          : `facilityId=${this.facilityId}&specialtyId=${this.specialtyId}&invoiceDate=${this.invoiceDate}`;
      const checklistItems = await (this as any).httpGet(`/invoices/checklist-items?${params}`, 'Error retrieving Checklist Items', false) || [];
      if (!checklistItems) {
        return false;
      }
      const remaining = checklistItems.map((item: any) => item.isChecked ? 0 : 1).reduce((a: number, b: number) => a + b, 0);
      if (remaining > 0) {
        this.showConfirmationModal = true;
        return false;
      }
      return true;
    },
    async submitToFacility() {
      const messageConfirmation = await this.submit("emails");
      if (messageConfirmation) {
        this.submissionEmailSentMessage = messageConfirmation.replace(/[;,]/g, ",<br/>");
        this.showSubmissionEmailsSentModal = true;
      }
    },
    clearEmailSentModal() {
      this.submissionEmailSentMessage = '';
      this.showSubmissionEmailsSentModal = false;
    },
    async submitQuietly() {
      await this.submit("submission")
    },
    async submit(pathFragment: string) {
      let showLoadingSpinnerTimeout = setTimeout(() => {
        this.loadingInitialData = true;
      }, 0);
      try {
        const response = await HTTP.post(`/api/invoices/${(this.invoiceInfo as any).invoiceId}/${pathFragment}`);
        clearTimeout(showLoadingSpinnerTimeout);
        this.loadingInitialData = false;
        if (response.data && !response.data.success && response.data.message) {
          this.$notify({
            group: 'main',
            type: 'error',
            title: 'Error',
            text: response.data.message
          });
        } else if (!response.data || !response.data.data) {
          throw new Error("Error Submitting Invoice.");
        } else {
          await this.getInvoiceInfo();
          return response.data.message;
        }
      } catch (err) {
        clearTimeout(showLoadingSpinnerTimeout);
        this.loadingInitialData = false;
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred submitting the Invoice.'
        });
      }
    },
    async changeFrozen() {
      let showLoadingSpinnerTimeout = setTimeout(() => {
        this.loadingInitialData = true;
      }, 0);
      try {
        const response = await HTTP.put(`/api/invoices/${(this.invoiceInfo as any).invoiceId}`, {isFrozen: !(this.invoiceInfo as any).isFrozen});
        clearTimeout(showLoadingSpinnerTimeout);
        this.loadingInitialData = false;
        if (response.data && !response.data.success && response.data.message) {
          this.$notify({
            group: 'main',
            type: 'error',
            title: 'Error',
            text: response.data.message
          });
          return;
        } else if (!response.data || !response.data.data) {
          throw new Error("Error Revising Invoice.");
        }
        InvoiceEventHub.$emit('INVOICE_CHANGE');
        await this.getInvoiceInfo();
      } catch (err) {
        console.error(err);
        clearTimeout(showLoadingSpinnerTimeout);
        this.loadingInitialData = false;
        let errorMessage = 'An error occurred Revizing the Invoice.';
        if (err.response.data && err.response.data.message && err.response.data.message.length) {
          errorMessage = err.response.data.message;
        }
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: errorMessage
        });
      }
    },
    async changeLocked() {
      let showLoadingSpinnerTimeout = setTimeout(() => {
        this.loadingInitialData = true;
      }, 0);
      try {
        const response = await HTTP.put(`/api/invoices/${(this.invoiceInfo as any).invoiceId}`, {isLocked: false});
        clearTimeout(showLoadingSpinnerTimeout);
        this.loadingInitialData = false;
        if (response.data && !response.data.success && response.data.message) {
          this.$notify({
            group: 'main',
            type: 'error',
            title: 'Error',
            text: response.data.message
          });
          return;
        } else if (!response.data || !response.data.data) {
          throw new Error("Error Unlocking Invoice.");
        }
        InvoiceEventHub.$emit('INVOICE_CHANGE');
        await this.getInvoiceInfo();
      } catch (err) {
        console.error(err);
        clearTimeout(showLoadingSpinnerTimeout);
        this.loadingInitialData = false;
        let errorMessage = 'An error occurred unlocking the Invoice.';
        if (err.response.data && err.response.data.message && err.response.data.message.length) {
          errorMessage = err.response.data.message;
        }
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: errorMessage
        });
      }
    },
    async openAttachment() {
      if (!this.pdfInfo) {
        return;
      }
      try {
        const response = await HTTP.get('/api/s3/signed-url/get-secure?s3key=' + (this.pdfInfo as any).s3Key);
        if (!response.data || !response.data.data) {
          throw new Error("Error getting attachment.");
        }
        window.open(response.data.data, '_blank');
      } catch (err) {
        console.error(err);
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred saving uploaded file attachment.'
        });
      }
    },
    async goToMergeView() {
      const pushObject = this.mergedInvoiceId
        ? {name: 'UpdatePreInvoiceMerge', params: {mergedInvoiceId: this.mergedInvoiceId}}
        : {
          name: 'SelectedPreInvoiceMerge',
          params: {
            facilityId: this.facilityId,
            specialtyId: this.specialtyId,
            invoiceDate: this.invoiceDate,
          }
        };
      this.$router.push(pushObject as any);
    },
    async toggleIgnoreQbSync(ignoreQbSync: boolean, confirmed = false) {
      if (ignoreQbSync && !confirmed) {
        this.showIgnoreQbSyncConfirmationModal = true;
        return;
      }
      this.showIgnoreQbSyncConfirmationModal = false;
      const url = `api/invoices/${this.invoiceId}/ignore-qb-sync-policy`;
      try {
        await HTTP.put(url, {ignoreQbSync: ignoreQbSync});
        await this.getInvoiceInfo();
      } catch (err) {
        let errorMessage = 'An error occurred updating Quickbooks ignore policy.';
        this.invoiceInfo.ignoreQbSync = !ignoreQbSync;
        if (err.response.data && err.response.data.message && err.response.data.message.length) {
          errorMessage = err.response.data.message;
        }
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: errorMessage
        });
      }
    },
    undoToggleIgnoreQbSync() {
      this.invoiceInfo.ignoreQbSync = false;
      this.showIgnoreQbSyncConfirmationModal = false;
    }
  }
})
;



// WEBPACK FOOTER //
// ./src/components/invoice/cards/info/InvoiceInfoCard.ts